import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
  InstanceResponseDto,
  RegistrationStatus,
  SnapshotResponseDto,
} from '@cloud-twin-v2/generated/simulation.generated.client';
import { GridSetting } from '@common-modules/shared/constants/grid.constants';
import { GridSettingsService } from '@common-modules/shared/core/grid/grid-settings.service';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { LocalGridComponent } from '@common-modules/wlm-grid/local-grid/local-grid.component';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize, of, switchMap } from 'rxjs';
import { ModelVersionSharedService } from '../services/model-version-shared.service';
import { ModelVersionService } from '../services/model-version.service';

const COMPONENT_SELECTOR = 'tc-model-version-grid';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './model-version-grid.component.html',
  styleUrl: './model-version-grid.component.scss',
  standalone: false,
})
export class ModelVersionGridComponent implements OnInit {
  @ViewChild(LocalGridComponent) grid: LocalGridComponent;

  private _selectedInstance: InstanceResponseDto;
  public get selectedInstance(): InstanceResponseDto {
    return this._selectedInstance;
  }

  @Input() public set selectedInstance(value: InstanceResponseDto) {
    this._selectedInstance = value;
    this.extractSnapshots();
  }

  @Output() selectedSnapshot = new EventEmitter<SnapshotResponseDto>();
  @Output() selectedSnapshots = new EventEmitter<SnapshotResponseDto[]>();

  selectedSnapshot1: SnapshotResponseDto;
  instanceElement: InstanceResponseDto;
  snapshots: SnapshotResponseDto[];
  gridSettings: GridSetting;

  private _selectedItems: SnapshotResponseDto[];
  public get selectedItems(): SnapshotResponseDto[] {
    return this._selectedItems;
  }
  public set selectedItems(value: SnapshotResponseDto[]) {
    this._selectedItems = value;
    this.onSelectedItemsChange(this._selectedItems);
  }

  private readonly _gridSettingsKey = 'ModelVersion';

  private readonly _gridSettingsService = inject(GridSettingsService);
  private readonly _modelVersionService = inject(ModelVersionService);
  private readonly _modelVersionSharedService = inject(ModelVersionSharedService);
  private readonly _dialogService = inject(DialogService);
  private readonly _translate = inject(TranslateService);
  private setLoading: (loading: boolean) => void;

  constructor(private readonly _spinnerService: SpinnerService) {
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  ngOnInit(): void {
    this._gridSettingsService
      .getGridSettingsByName(this._gridSettingsKey)
      .subscribe((gridSettings) => {
        this.gridSettings = gridSettings;
      });
  }

  onSelectedItem(snapshot: SnapshotResponseDto): void {
    this.selectedSnapshot.emit(snapshot);
  }

  onSelectedItemsChange(items: SnapshotResponseDto[]): void {
    this.selectedSnapshots.emit(items);
  }

  reloadItemsGrid(): void {
    this.snapshots = [];
  }

  snapshotsToDelete(snapshotsIds: string[]): void {
    if (this.snapshots) {
      this.snapshots = this.snapshots.filter(
        (snapshot) => !snapshotsIds.includes(snapshot.snapshotId)
      );
    }
  }

  updateScenariosOfSnapshot(snapshotId: string, scenarioIds: string[]) {
    this.snapshots = this.snapshots.map((snapshot) => {
      if (snapshot.snapshotId !== snapshotId) {
        return snapshot;
      }

      snapshot.scenarios = snapshot.scenarios?.filter(
        (scenario) => !scenarioIds.includes(scenario.scenarioId)
      );

      return snapshot;
    });
  }

  private extractSnapshots(): void {
    if (this._selectedInstance && this._selectedInstance.snapshots) {
      this.snapshots = this._selectedInstance.snapshots;

      this.snapshots = this._selectedInstance.snapshots.map((snapshot) => {
        const hasOnlineScenario = snapshot.scenarios?.some(
          (scenario) => scenario.status === RegistrationStatus.Online
        );

        return { ...snapshot, status: hasOnlineScenario } as any;
      });

      if (this.snapshots.length !== 1) {
        this.onSelectedItem(new SnapshotResponseDto({}));
      }
    } else {
      this.snapshots = [];
      this.onSelectedItem(new SnapshotResponseDto({}));
    }
  }

  deleteSnapshots(): void {
    const instanceId = this.selectedItems[0].instanceId;
    const snapshotIds = this.selectedItems.map((snapshot) => snapshot.snapshotId);
    const modelKey = this.getModelKey(snapshotIds);
    const confirmMessageKey = this.getConfirmDeleteMessage(modelKey);

    this._dialogService
      .showTranslatedDialogMessage({
        translateKey: confirmMessageKey,
        icon: 'warning',
      })
      .pipe(
        switchMap((result) => {
          if (result.result) {
            this.setLoading(true);
            return this._modelVersionService.deleteSnapshots(instanceId, snapshotIds);
          } else {
            return of(null);
          }
        }),
        finalize(() => {
          this.setLoading(false);
        })
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this._modelVersionSharedService.removeSnapshots(snapshotIds);
            this._modelVersionSharedService.removeScenarios(result);
            this._modelVersionSharedService.updateSnapshotsOfInstance(instanceId, snapshotIds);
            this._dialogService.showEntityActionSnackBar('delete', modelKey);
          }
        },
        error: (error) => {
          this._dialogService.showErrorMessage(error);
        },
      });
  }

  getConfirmDeleteMessage(entityName: string): string {
    const entityNameTranslated = this._translate.instant(`common.entities.${entityName}`);
    return this._translate.instant(this.getConfirmDeleteMessageKey(), {
      entityName: entityNameTranslated,
    });
  }

  private getConfirmDeleteMessageKey(): string {
    return 'core.messages.delete-confirm';
  }

  private getModelKey(items: any[]): 'model-version.singular' | 'model-version.plural' {
    return items.length > 1 ? 'model-version.plural' : 'model-version.singular';
  }
}
